// POA certification by a lawyer outside Coepi (lawyer-backend
// partners_app/certification). The certifier side is /certifications/*; the
// lawyer reads the result on the case and can send a case back.

import { ApiError, errorMessageFrom, request } from './client';

export interface CertificationQueueItem {
  case_id: string;
  name: string;
  minor: boolean;
  lawyer_status: string;
  poa_signed_at: string | null;
  days_waiting: number | null;
  filed_before?: boolean;
  /** The POA changed after an earlier certification or decline. */
  resigned: boolean;
}

export interface CertificationDoneItem {
  case_id: string;
  name: string;
  minor: boolean;
  status: 'certified' | 'declined';
  done_at: string | null;
  registration_number?: string | null;
}

export interface CertificationQueue {
  items: CertificationQueueItem[];
  done: CertificationDoneItem[];
}

export interface CertificationDocument {
  kind: 'poa' | 'audit_log' | 'identity';
  label: string;
  url: string;
}

/** Someone who signed the POA, for the Ordem's "Interessado(s)". */
export interface InterestedParty {
  /** "Passport" or "ID card"; null when the case does not say. */
  document: string | null;
  number: string | null;
  name: string | null;
}

export interface CertificationCase {
  case_id: string;
  name: string;
  minor: boolean;
  poa_signed_on: string | null;
  /** Signed in ink and attached as a scan: no audit log. Missing from an older backend. */
  hand_signed?: boolean;
  documents: CertificationDocument[];
  /** Missing from an older backend. */
  interested_parties?: InterestedParty[];
  /** The text the certifier pastes into "Resumo do ato" on the Ordem's portal. */
  summary: string;
  /** Their cédula or address is not saved yet, so the summary has gaps. */
  details_missing: boolean;
}

/** The poa_certification field on a case, as the lawyer sees it. */
export interface PoaCertification {
  status: 'certified' | 'declined';
  poa_file_id?: string;
  certifier_name?: string;
  certifier_email?: string;
  cedula?: string;
  registration_number?: string;
  consultation_code?: string;
  digitally_signed?: boolean;
  reason?: string;
  certified_at?: any;
  declined_at?: any;
}

export interface OaNumbers {
  registration_number: string;
  consultation_code: string;
}

const apiUrl = (path: string) => `${process.env.REACT_APP_API_URL}${path}`;
const casePath = (caseId: string) => `/certifications/${encodeURIComponent(caseId)}`;

const fail = async (response: Response, fallback: string): Promise<never> => {
  let data: any = null;
  try {
    data = await response.json();
  } catch {
    // Not JSON: keep the fallback line.
  }
  throw new ApiError(errorMessageFrom(data, fallback), response.status, data);
};

export const getCertificationQueue = async (token: string): Promise<CertificationQueue> => {
  const data = await request(token, '/certifications/queue', { method: 'GET' }, 'Could not load the queue.');
  return { items: data?.items ?? [], done: data?.done ?? [] };
};

export interface CertificationMonth {
  /** YYYY-MM */
  month: string;
  /** Cases certified for the first time: what is billed. */
  certified: number;
  /** A case certified again after an earlier certification: not billed. */
  repeats?: number;
  declined: number;
}

export const getCertificationStats = async (token: string): Promise<CertificationMonth[]> => {
  const data = await request(token, '/certifications/stats', { method: 'GET' }, 'Could not load the statistics.');
  return data?.months ?? [];
};

export interface CertifierDetails {
  salutation: string;
  name: string;
  title: string;
  cedula: string;
  address: string;
}

export const getCertifierDetails = async (token: string): Promise<CertifierDetails> =>
  request(token, '/certifications/me', { method: 'GET' }, 'Could not load your details.');

export const saveCertifierDetails = async (token: string, details: CertifierDetails): Promise<CertifierDetails> =>
  request(token, '/certifications/me', { method: 'PUT', body: JSON.stringify(details) }, 'Could not save your details.');

export const getCertificationCase = async (token: string, caseId: string): Promise<CertificationCase> =>
  request(token, casePath(caseId), { method: 'GET' }, 'Could not load this case.');

// Multipart, so it does not go through request(), which sets a JSON content type.
export const submitCertification = async (
  token: string,
  caseId: string,
  numbers: OaNumbers,
  signedPdf: File
): Promise<{ status: 'certified'; digitally_signed: boolean }> => {
  const body = new FormData();
  body.append('registration_number', numbers.registration_number);
  body.append('consultation_code', numbers.consultation_code);
  body.append('file', signedPdf);
  const response = await fetch(apiUrl(casePath(caseId)), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  if (!response.ok) return fail(response, 'Could not save the certification.');
  return response.json();
};

export const declineCertification = async (token: string, caseId: string, reason: string) =>
  request(token, `${casePath(caseId)}/decline`, { method: 'POST', body: JSON.stringify({ reason }) },
    'Could not save the decline.');

// Lawyer side.
export const getPoaCertificationUrl = async (token: string, caseId: string): Promise<string> => {
  const data = await request(token, `/cases/${encodeURIComponent(caseId)}/poa-certification/signed-url`,
    { method: 'GET' }, 'Could not open the certification.');
  return data.url;
};

/** The certification, the POA and the audit log as one PDF, named after the customer. */
export const downloadCertifiedPoa = async (token: string, caseId: string): Promise<{ blob: Blob; fileName: string }> => {
  const response = await fetch(apiUrl(`/cases/${encodeURIComponent(caseId)}/poa-certification/pdf`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) return fail(response, 'Could not download the certified POA.');
  const disposition = response.headers.get('Content-Disposition') || '';
  const fileName = /filename="?([^";]+)"?/.exec(disposition)?.[1] || `${caseId}_certified_POA.pdf`;
  return { blob: await response.blob(), fileName };
};

export const resetPoaCertification = async (token: string, caseId: string) =>
  request(token, `/cases/${encodeURIComponent(caseId)}/poa-certification`, { method: 'DELETE' },
    'Could not send the POA back to the queue.');
